import { BankAccount } from '../Model/BankAccount';
import { Company } from '../Model/Company';
import { Statistics } from '../Model/Statistics';
import { storeApi } from './storeApi';

type IncomingBankAccount = {
  accounts: Omit<BankAccount, 'statistics'>[];
  statistics: Statistics[];
};
export const bankAccountApi = storeApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankAccountList: builder.query<BankAccount[], Partial<Company>>({
      query: ({ _id }) => {
        return {
          url: '/bankaccounts',
          params: { company_id: _id },
        };
      },
      transformResponse: (response: IncomingBankAccount) => {
        return response?.accounts?.map((original) => ({
          _id: `${original.accountNr}-${original.originate}`,
          ...original,
          statistics: response.statistics.filter(
            (stat) => stat.accountNr === original.accountNr && stat.originate === original.originate
          ),
        }));
      },
      providesTags: (result) => {
        return result
          ? [...result.map(({ _id }) => ({ type: 'BankAccount', _id } as const)), { type: 'BankAccount', id: 'LIST' }]
          : [{ type: 'BankAccount', id: 'LIST' }];
      },
    }),
    editBankAccountName: builder.mutation<Partial<BankAccount>[], Partial<BankAccount>>({
      query: ({ accountNr, displayName }) => {
        return {
          url: `/bankaccount/${accountNr}/${displayName}`,
          method: 'PATCH',
        };
      },
      invalidatesTags: [{ type: 'BankAccount', id: 'LIST' }],
    }),
    movedBankAccountToAnotherCompany: builder.mutation<
      void,
      Pick<BankAccount, 'accountNr' | 'originate'> & { sourceCompanyId: string; targetCompanyId: string }
    >({
      query: ({ accountNr, originate, sourceCompanyId, targetCompanyId }) => ({
        url: `/bankaccount`,
        method: 'PATCH',
        body: {
          account_nr: accountNr,
          originate,
          source_company_id: sourceCompanyId,
          target_company_id: targetCompanyId,
        },
      }),
      invalidatesTags: [{ type: 'BankAccount', id: 'LIST' }],
    }),
    deleteBankAccount: builder.mutation<void, Pick<BankAccount, 'accountNr' | 'originate'> & Pick<Company, '_id'>>({
      query: ({ accountNr, originate, _id }) => ({
        url: `/bankaccount`,
        method: 'DELETE',
        body: { account_nr: accountNr, originate, company_id: _id },
      }),
      invalidatesTags: [{ type: 'BankAccount', id: 'LIST' }],
    }),
    deleteIntegrationsByAccountantId: builder.mutation<void, Partial<BankAccount>>({
      query(data) {
        const { accountId, companyId } = data;
        return {
          url: '/aggreg8/isc',
          method: 'DELETE',
          body: { account_id: accountId, company_id: companyId },
        };
      },
      invalidatesTags: [{ type: 'BankAccount', id: 'LIST' }],
    }),
  }),
});

export const {
  useLazyGetBankAccountListQuery,
  useEditBankAccountNameMutation,
  useDeleteBankAccountMutation,
  useDeleteIntegrationsByAccountantIdMutation,
  useMovedBankAccountToAnotherCompanyMutation,
} = bankAccountApi;
